/**
 * Pre-Construction Service
 * Handles BOQ, Estimates, Bids, and Proposals API calls
 */

import { httpClient, ApiResponse } from './http.client';

// =================== INTERFACES ===================

export interface BOQItem {
  boq_item_id: string;
  boq_id: string;
  item_number: string;
  parent_item_id?: string;
  level: number;
  description: string;
  specification?: string;
  work_category: 'sitework' | 'concrete' | 'masonry' | 'metals' | 'wood_plastics' | 'thermal_moisture' | 'openings' | 'finishes' | 'specialties' | 'equipment' | 'furnishings' | 'conveying' | 'fire_suppression' | 'plumbing' | 'hvac' | 'electrical' | 'communications' | 'electronic_safety' | 'earthwork' | 'utilities';
  quantity: number;
  unit_of_measure: 'each' | 'lf' | 'sf' | 'cf' | 'sy' | 'cy' | 'lbs' | 'tons' | 'hours' | 'days' | 'ls';
  unit_rate?: number;
  total_amount?: number;
  labor_cost?: number;
  material_cost?: number;
  equipment_cost?: number;
  overhead_percentage?: number;
  profit_percentage?: number;
  remarks?: string;
  drawing_reference?: string;
  created_at: string;
  updated_at: string;
}

export interface BOQ {
  boq_id: string;
  organization_id: string;
  project_id: string;
  bid_id?: string;
  boq_number: string;
  boq_name: string;
  description?: string;
  version: string;
  project_name?: string;
  project_location?: string;
  client_name?: string;
  total_amount: number;
  contingency_percentage?: number;
  contingency_amount?: number;
  final_amount?: number;
  currency: string;
  exchange_rate?: number;
  status: 'draft' | 'in_progress' | 'under_review' | 'approved' | 'rejected' | 'final';
  preparation_date?: string;
  validity_period?: number;
  items: BOQItem[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface EstimateItem {
  estimate_item_id: string;
  estimate_id: string;
  item_code?: string;
  description: string;
  work_category: string;
  cost_type: 'labor' | 'material' | 'equipment' | 'subcontractor' | 'other';
  quantity: number;
  unit_of_measure: string;
  unit_rate: number;
  total_cost: number;
  crew_size?: number;
  productivity_rate?: number;
  labor_hours?: number;
  labor_rate?: number;
  labor_cost?: number;
  material_cost?: number;
  equipment_cost?: number;
  waste_factor?: number;
  difficulty_factor?: number;
  source?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface Estimate {
  estimate_id: string;
  organization_id: string;
  project_id: string;
  estimate_number: string;
  estimate_name: string;
  description?: string;
  version: string;
  project_name: string;
  project_type?: string;
  project_location?: string;
  project_size?: string;
  client_name: string;
  client_contact?: string;
  estimate_date: string;
  base_date?: string;
  escalation_rate?: number;
  location_factor?: number;
  direct_costs: number;
  indirect_costs: number;
  overhead_percentage?: number;
  overhead_amount: number;
  profit_percentage?: number;
  profit_amount: number;
  contingency_percentage?: number;
  contingency_amount: number;
  total_estimate: number;
  accuracy_level?: string;
  confidence_level?: number;
  status: 'draft' | 'in_progress' | 'under_review' | 'approved' | 'rejected' | 'final';
  approved_by?: string;
  approved_at?: string;
  items: EstimateItem[];
  assumptions?: string;
  exclusions?: string;
  prepared_by: string;
  created_at: string;
  updated_at: string;
}

export interface BidItem {
  bid_item_id: string;
  bid_id: string;
  item_number: string;
  description: string;
  specification_reference?: string;
  quantity: number;
  unit_of_measure: string;
  unit_bid_price: number;
  total_bid_price: number;
  is_alternate: boolean;
  alternate_number?: string;
  is_optional: boolean;
  notes?: string;
  created_at: string;
}

export interface Bid {
  bid_id: string;
  organization_id: string;
  bid_number: string;
  project_name: string;
  bid_package?: string;
  owner_name: string;
  owner_contact?: string;
  architect_engineer?: string;
  bid_due_date: string;
  bid_opening_date?: string;
  submission_location?: string;
  project_location: string;
  project_description: string;
  contract_type?: string;
  project_duration?: number;
  base_bid_amount: number;
  alternate_amounts?: any;
  total_bid_amount: number;
  bid_bond_required: boolean;
  bid_bond_percentage?: number;
  performance_bond_required: boolean;
  payment_bond_required: boolean;
  prevailing_wage: boolean;
  mbe_wbe_requirements?: string;
  status: 'draft' | 'submitted' | 'under_review' | 'accepted' | 'rejected' | 'withdrawn';
  submitted_at?: string;
  submitted_by?: string;
  items: BidItem[];
  documents_submitted?: string[];
  prepared_by: string;
  created_at: string;
  updated_at: string;
}

export interface Proposal {
  proposal_id: string;
  organization_id: string;
  proposal_number: string;
  proposal_title: string;
  project_name: string;
  client_name: string;
  client_contact: string;
  client_email?: string;
  client_phone?: string;
  executive_summary?: string;
  scope_of_work: string;
  project_approach?: string;
  timeline?: string;
  project_team?: any[];
  company_qualifications?: string;
  relevant_experience?: string;
  references?: any[];
  total_project_cost: number;
  payment_schedule?: string;
  pricing_basis?: string;
  contract_terms?: string;
  warranty_terms?: string;
  exclusions?: string;
  assumptions?: string;
  proposal_date: string;
  valid_until: string;
  proposed_start_date?: string;
  proposed_completion_date?: string;
  status: 'draft' | 'submitted' | 'under_review' | 'accepted' | 'rejected' | 'expired';
  submitted_at?: string;
  presentation_scheduled?: string;
  client_response_date?: string;
  win_probability?: number;
  prepared_by: string;
  created_at: string;
  updated_at: string;
}

export interface Takeoff {
  takeoff_id: string;
  organization_id: string;
  project_id?: string;
  estimate_id?: string;
  takeoff_name: string;
  description?: string;
  work_category: string;
  drawing_name?: string;
  drawing_revision?: string;
  scale?: string;
  measurements: any[];
  total_quantity: number;
  unit_of_measure: string;
  waste_factor?: number;
  adjusted_quantity?: number;
  takeoff_method?: string;
  software_used?: string;
  checked_by?: string;
  checked_at?: string;
  accuracy_notes?: string;
  performed_by: string;
  created_at: string;
  updated_at: string;
}

// =================== PRE-CONSTRUCTION SERVICE CLASS ===================

class PreConstructionService {
  // =================== BOQ (BILL OF QUANTITIES) ===================
  
  async getBOQs(projectId?: string, page = 1, perPage = 20): Promise<ApiResponse<{ data: BOQ[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });

    if (projectId) {
      queryParams.append('project_id', projectId);
    }

    return httpClient.get<{ data: BOQ[]; total: number }>(
      `/api/v1/boqs/?${queryParams}`
    );
  }

  async getBOQ(boqId: string): Promise<ApiResponse<BOQ>> {
    return httpClient.get<BOQ>(`/api/v1/boqs/${boqId}`);
  }

  async getBOQItems(boqId: string): Promise<ApiResponse<BOQItem[]>> {
    return httpClient.get<BOQItem[]>(`/api/v1/boqs/${boqId}/items`);
  }

  async createBOQ(boqData: Partial<BOQ>): Promise<ApiResponse<BOQ>> {
    return httpClient.post<BOQ>('/api/v1/boqs/', boqData);
  }

  async updateBOQ(boqId: string, boqData: Partial<BOQ>): Promise<ApiResponse<BOQ>> {
    return httpClient.put<BOQ>(`/api/v1/boqs/${boqId}`, boqData);
  }

  async deleteBOQ(boqId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/boqs/${boqId}`);
  }

  async duplicateBOQ(boqId: string, newName: string): Promise<ApiResponse<BOQ>> {
    return httpClient.post<BOQ>(`/api/v1/boqs/${boqId}/duplicate`, { name: newName });
  }

  async exportBOQ(boqId: string, format: 'pdf' | 'excel' | 'csv'): Promise<ApiResponse<{ download_url: string }>> {
    return httpClient.post<{ download_url: string }>(`/api/v1/boqs/${boqId}/export`, { format });
  }

  // =================== ESTIMATES ===================

  async getEstimates(projectId?: string, page = 1, perPage = 20): Promise<ApiResponse<{ data: Estimate[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (projectId) {
      queryParams.append('project_id', projectId);
    }

    return httpClient.get<{ data: Estimate[]; total: number }>(
      `/api/v1/estimates?${queryParams}`
    );
  }

  async getEstimate(estimateId: string): Promise<ApiResponse<Estimate>> {
    return httpClient.get<Estimate>(`/api/v1/estimates/${estimateId}`);
  }

  async createEstimate(estimateData: Partial<Estimate>): Promise<ApiResponse<Estimate>> {
    return httpClient.post<Estimate>('/api/v1/estimates', estimateData);
  }

  async updateEstimate(estimateId: string, estimateData: Partial<Estimate>): Promise<ApiResponse<Estimate>> {
    return httpClient.put<Estimate>(`/api/v1/estimates/${estimateId}`, estimateData);
  }

  async deleteEstimate(estimateId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/estimates/${estimateId}`);
  }

  async approveEstimate(estimateId: string): Promise<ApiResponse<Estimate>> {
    return httpClient.post<Estimate>(`/api/v1/estimates/${estimateId}/approve`, {});
  }

  async convertEstimateToBOQ(estimateId: string, boqName: string): Promise<ApiResponse<BOQ>> {
    return httpClient.post<BOQ>(`/api/v1/estimates/${estimateId}/convert-to-boq`, { boq_name: boqName });
  }

  async getEstimateComparison(estimateIds: string[]): Promise<ApiResponse<any>> {
    return httpClient.post<any>('/api/v1/estimates/compare', { estimate_ids: estimateIds });
  }

  // =================== BIDS ===================

  async getBids(page = 1, perPage = 20, filters?: any): Promise<ApiResponse<{ data: Bid[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters
    });

    return httpClient.get<{ data: Bid[]; total: number }>(
      `/api/v1/bids?${queryParams}`
    );
  }

  async getBid(bidId: string): Promise<ApiResponse<Bid>> {
    return httpClient.get<Bid>(`/api/v1/bids/${bidId}`);
  }

  async createBid(bidData: Partial<Bid>): Promise<ApiResponse<Bid>> {
    return httpClient.post<Bid>('/api/v1/bids', bidData);
  }

  async updateBid(bidId: string, bidData: Partial<Bid>): Promise<ApiResponse<Bid>> {
    return httpClient.put<Bid>(`/api/v1/bids/${bidId}`, bidData);
  }

  async deleteBid(bidId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/bids/${bidId}`);
  }

  async submitBid(bidId: string): Promise<ApiResponse<Bid>> {
    return httpClient.post<Bid>(`/api/v1/bids/${bidId}/submit`, {});
  }

  async withdrawBid(bidId: string, reason?: string): Promise<ApiResponse<Bid>> {
    return httpClient.post<Bid>(`/api/v1/bids/${bidId}/withdraw`, { reason });
  }

  async generateBidDocument(bidId: string, templateId?: string): Promise<ApiResponse<{ document_url: string }>> {
    return httpClient.post<{ document_url: string }>(`/api/v1/bids/${bidId}/generate-document`, { template_id: templateId });
  }

  // =================== PROPOSALS ===================

  async getProposals(page = 1, perPage = 20, filters?: any): Promise<ApiResponse<{ data: Proposal[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters
    });

    return httpClient.get<{ data: Proposal[]; total: number }>(
      `/api/v1/proposals?${queryParams}`
    );
  }

  async getProposal(proposalId: string): Promise<ApiResponse<Proposal>> {
    return httpClient.get<Proposal>(`/api/v1/proposals/${proposalId}`);
  }

  async createProposal(proposalData: Partial<Proposal>): Promise<ApiResponse<Proposal>> {
    return httpClient.post<Proposal>('/api/v1/proposals', proposalData);
  }

  async updateProposal(proposalId: string, proposalData: Partial<Proposal>): Promise<ApiResponse<Proposal>> {
    return httpClient.put<Proposal>(`/api/v1/proposals/${proposalId}`, proposalData);
  }

  async deleteProposal(proposalId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/proposals/${proposalId}`);
  }

  async submitProposal(proposalId: string): Promise<ApiResponse<Proposal>> {
    return httpClient.post<Proposal>(`/api/v1/proposals/${proposalId}/submit`, {});
  }

  async generateProposalDocument(proposalId: string, templateId?: string): Promise<ApiResponse<{ document_url: string }>> {
    return httpClient.post<{ document_url: string }>(`/api/v1/proposals/${proposalId}/generate-document`, { template_id: templateId });
  }

  async sendProposalToClient(proposalId: string, recipientEmail?: string): Promise<ApiResponse<Proposal>> {
    return httpClient.post<Proposal>(`/api/v1/proposals/${proposalId}/send`, { recipient_email: recipientEmail });
  }

  // =================== TAKEOFFS ===================

  async getTakeoffs(projectId?: string, estimateId?: string, page = 1, perPage = 20): Promise<ApiResponse<{ data: Takeoff[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (projectId) queryParams.append('project_id', projectId);
    if (estimateId) queryParams.append('estimate_id', estimateId);

    return httpClient.get<{ data: Takeoff[]; total: number }>(
      `/api/v1/takeoffs?${queryParams}`
    );
  }

  async getTakeoff(takeoffId: string): Promise<ApiResponse<Takeoff>> {
    return httpClient.get<Takeoff>(`/api/v1/takeoffs/${takeoffId}`);
  }

  async createTakeoff(takeoffData: Partial<Takeoff>): Promise<ApiResponse<Takeoff>> {
    return httpClient.post<Takeoff>('/api/v1/takeoffs', takeoffData);
  }

  async updateTakeoff(takeoffId: string, takeoffData: Partial<Takeoff>): Promise<ApiResponse<Takeoff>> {
    return httpClient.put<Takeoff>(`/api/v1/takeoffs/${takeoffId}`, takeoffData);
  }

  async deleteTakeoff(takeoffId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/takeoffs/${takeoffId}`);
  }

  async importTakeoffFromDrawing(drawingFile: File, projectId: string): Promise<ApiResponse<Takeoff>> {
    const formData = new FormData();
    formData.append('drawing', drawingFile);
    formData.append('project_id', projectId);
    
    return httpClient.post<Takeoff>('/api/v1/takeoffs/import-drawing', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  // =================== REPORTING & ANALYTICS ===================

  async getPreConstructionDashboard(organizationId?: string): Promise<ApiResponse<any>> {
    const queryParams = organizationId ? `?organization_id=${organizationId}` : '';
    return httpClient.get<any>(`/api/v1/pre-construction/dashboard${queryParams}`);
  }

  async getBidPerformanceAnalysis(dateRange?: { start: string; end: string }): Promise<ApiResponse<any>> {
    const queryParams = dateRange ? new URLSearchParams({
      start_date: dateRange.start,
      end_date: dateRange.end
    }) : '';
    
    return httpClient.get<any>(`/api/v1/pre-construction/bid-performance?${queryParams}`);
  }

  async getEstimatingAccuracy(estimatorId?: string, timeframe = '12months'): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams({ timeframe });
    if (estimatorId) queryParams.append('estimator_id', estimatorId);
    
    return httpClient.get<any>(`/api/v1/pre-construction/estimating-accuracy?${queryParams}`);
  }

  async getWinLossReport(dateRange?: { start: string; end: string }): Promise<ApiResponse<any>> {
    const queryParams = dateRange ? new URLSearchParams({
      start_date: dateRange.start,
      end_date: dateRange.end
    }) : '';
    
    return httpClient.get<any>(`/api/v1/pre-construction/win-loss?${queryParams}`);
  }

  // =================== UTILITY METHODS ===================

  async searchEstimates(query: string, projectId?: string): Promise<ApiResponse<{ data: Estimate[] }>> {
    const queryParams = new URLSearchParams({ q: query });
    if (projectId) queryParams.append('project_id', projectId);
    
    return httpClient.get<{ data: Estimate[] }>(
      `/api/v1/estimates/search?${queryParams}`
    );
  }

  async getEstimateTemplates(): Promise<ApiResponse<{ data: any[] }>> {
    return httpClient.get<{ data: any[] }>('/api/v1/estimates/templates');
  }

  async createEstimateFromTemplate(templateId: string, projectData: any): Promise<ApiResponse<Estimate>> {
    return httpClient.post<Estimate>(`/api/v1/estimates/from-template/${templateId}`, projectData);
  }

  async validateBOQTotals(boqId: string): Promise<ApiResponse<{ is_valid: boolean; errors?: string[] }>> {
    return httpClient.post<{ is_valid: boolean; errors?: string[] }>(`/api/v1/boqs/${boqId}/validate`, {});
  }

  async getHistoricalBidData(projectType?: string, location?: string): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams();
    if (projectType) queryParams.append('project_type', projectType);
    if (location) queryParams.append('location', location);
    
    return httpClient.get<any>(`/api/v1/pre-construction/historical-data?${queryParams}`);
  }
}

// Export singleton instance
export const preConstructionService = new PreConstructionService();